import React, { useState } from 'react';
import { Grid, Box, IconButton } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import InputField from '../../../../../../../components/formFields/InputField';
import AutocompleteField from 'components/formFields/autocompleteField';
import { InfoModal } from 'components/generics/InfoModal';
import { currencies } from 'commons/constants/currencies';

interface Props {
  formField: any;
}

const aiProviders = [
  { value: 'OPENAI', label: 'OpenAI' },
  { value: 'GEMINI', label: 'Gemini' },
  { value: 'ANTHROPIC', label: 'Anthropic' },
  { value: 'DEEPSEEK', label: 'DeepSeek' },
  { value: 'XAI', label: 'xAI' },
];

const aiServiceTypes = [
  { value: 'CHAT', label: 'Chat' },
  { value: 'IMAGE', label: 'Imagen' },
  { value: 'AUDIO', label: 'Audio' },
  { value: 'EMBEDDING', label: 'Embedding' },
  { value: 'OCR', label: 'OCR' },
  { value: 'VIDEO', label: 'Video' },
];

export const ServicesIaForm = ({ formField }: Props) => {
  const {
    provider,
    serviceType,
    name,
    code,
    description,
    apiVersion,
    currency,
    inputPrice,
    outputPrice,
    cachePrice,
    contextWindow,
    maxOutputTokens,
    sortOrder,
  } = formField;

  const [activeModal, setActiveModal] = useState<string | null>(null);

  const getModalContent = () => {
    switch (activeModal) {
      case 'provider':
        return {
          title: 'Proveedor',
          description: 'Proveedor de la API de IA.',
          infoText:
            'Ejemplo: Si seleccionas OpenAI, la conexión se hará con los servidores de OpenAI para utilizar sus modelos.',
        };
      case 'serviceType':
        return {
          title: 'Tipo de Servicio',
          description: 'El tipo de servicio que ofrece este modelo.',
          infoText: 'Ejemplo: Selecciona CHAT para configurar un asistente conversacional.',
        };
      case 'name':
        return {
          title: 'Nombre',
          description: 'Nombre descriptivo o comercial del modelo.',
          infoText:
            'Ejemplo: Asistente Avanzado (Gemini 1.5 Pro / GPT-4o) para que los usuarios lo identifiquen fácilmente.',
        };
      case 'code':
        return {
          title: 'Código Técnico',
          description: 'El identificador exacto esperado por la API.',
          infoText: 'Ejemplo: gemini-1.5-pro o gpt-4o.',
        };
      case 'description':
        return {
          title: 'Descripción',
          description: 'Una breve descripción de lo que hace el modelo y sus casos de uso.',
          infoText: 'Ejemplo: Ideal para tareas que requieren razonamiento complejo.',
        };
      case 'apiVersion':
        return {
          title: 'Versión de API',
          description: 'Versión de la API si aplica.',
          infoText: 'Ejemplo: 2024-02-15-preview (Muy común en Azure OpenAI).',
        };
      case 'currency':
        return {
          title: 'Moneda',
          description: 'Moneda en la que se factura el uso del modelo.',
          infoText:
            'Ejemplo: USD, ya que la mayoría de proveedores internacionales facturan en dólares.',
        };
      case 'inputPrice':
        return {
          title: 'Precio de Entrada',
          description: 'Costo por millón de tokens de entrada (Prompt).',
          infoText:
            'Ejemplo: 5.00 significa que cobrarán $5 USD por cada 1,000,000 de tokens enviados.',
        };
      case 'outputPrice':
        return {
          title: 'Precio de Salida',
          description: 'Costo por millón de tokens de salida (Completion).',
          infoText:
            'Ejemplo: 15.00 significa que cobrarán $15 USD por cada 1,000,000 de tokens devueltos.',
        };
      case 'cachePrice':
        return {
          title: 'Precio de Caché',
          description: 'Costo de tokens en caché, si el proveedor lo soporta.',
          infoText:
            'Ejemplo: En Anthropic, los tokens leídos desde la caché suelen tener un costo menor.',
        };
      case 'contextWindow':
        return {
          title: 'Ventana de Contexto',
          description: 'Tamaño máximo de la ventana de contexto en tokens.',
          infoText:
            'Ejemplo: 128000 para indicar que el modelo soporta aproximadamente 300 páginas de texto.',
        };
      case 'maxOutputTokens':
        return {
          title: 'Tokens Máximos de Salida',
          description: 'Cantidad máxima de tokens de salida permitidos.',
          infoText:
            'Ejemplo: 4096 es un límite común para asegurar respuestas completas pero controladas.',
        };
      case 'sortOrder':
        return {
          title: 'Orden de Visualización',
          description: 'Orden en el que aparecerá el servicio en las listas.',
          infoText: 'Ejemplo: 1 hará que este modelo sea el primero en mostrarse.',
        };
      default:
        return { title: '', description: '', infoText: '' };
    }
  };

  const modalContent = getModalContent();

  const renderFieldWithInfo = (field: React.ReactNode, modalKey: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flexGrow: 1 }}>{field}</Box>
      <IconButton color="info" onClick={() => setActiveModal(modalKey)}>
        <InfoOutlined />
      </IconButton>
    </Box>
  );

  return (
    <>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          {renderFieldWithInfo(
            <AutocompleteField
              name={provider.name}
              label={provider.label}
              options={aiProviders}
              fullWidth
            />,
            'provider'
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {renderFieldWithInfo(
            <AutocompleteField
              name={serviceType.name}
              label={serviceType.label}
              options={aiServiceTypes}
              fullWidth
            />,
            'serviceType'
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {renderFieldWithInfo(
            <InputField name={name.name} label={name.label} fullWidth />,
            'name'
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {renderFieldWithInfo(
            <InputField name={code.name} label={code.label} fullWidth />,
            'code'
          )}
        </Grid>
        <Grid size={{ xs: 12 }}>
          {renderFieldWithInfo(
            <InputField
              name={description.name}
              label={description.label}
              fullWidth
              multiline
              rows={3}
            />,
            'description'
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {renderFieldWithInfo(
            <InputField name={apiVersion.name} label={apiVersion.label} fullWidth />,
            'apiVersion'
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {renderFieldWithInfo(
            <AutocompleteField
              name={currency.name}
              label={currency.label}
              options={currencies}
              fullWidth
            />,
            'currency'
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          {renderFieldWithInfo(
            <InputField name={inputPrice.name} label={inputPrice.label} fullWidth type="number" />,
            'inputPrice'
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          {renderFieldWithInfo(
            <InputField
              name={outputPrice.name}
              label={outputPrice.label}
              fullWidth
              type="number"
            />,
            'outputPrice'
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          {renderFieldWithInfo(
            <InputField name={cachePrice.name} label={cachePrice.label} fullWidth type="number" />,
            'cachePrice'
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          {renderFieldWithInfo(
            <InputField
              name={contextWindow.name}
              label={contextWindow.label}
              fullWidth
              type="number"
            />,
            'contextWindow'
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          {renderFieldWithInfo(
            <InputField
              name={maxOutputTokens.name}
              label={maxOutputTokens.label}
              fullWidth
              type="number"
            />,
            'maxOutputTokens'
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          {renderFieldWithInfo(
            <InputField name={sortOrder.name} label={sortOrder.label} fullWidth type="number" />,
            'sortOrder'
          )}
        </Grid>
      </Grid>
      <InfoModal
        open={activeModal !== null}
        setOpen={(val: boolean) => {
          if (!val) setActiveModal(null);
        }}
        title={modalContent.title}
        description={modalContent.description}
        infoText={modalContent.infoText}
        icon="Info"
      />
    </>
  );
};
